import { AssetPreview, assetDetails, assetName } from '@/components/assets/asset-presenter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import {
  browserAssetExplorerStorage,
  readAssetExplorerView,
  writeAssetExplorerView,
  type AssetExplorerView,
} from '@/lib/asset-explorer-preferences';
import { isIconAsset } from '@/lib/icons';
import { MAX_ASSET_BYTES, useUploadAsset, type Asset } from '@/lib/queries';
import { findNode, findStep, type SessionStore } from '@personalizehq/editor';
import { Link } from '@tanstack/react-router';
import { FileType, Grid2X2, Image, Images, List, Search, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useStore } from 'zustand';

// `icons` is a VIEW over images, not a fourth kind of asset: an icon is an ordinary PNG that the
// picker minted (see `lib/icons.ts`), which is exactly what lets it inherit revisions, usage
// tracking, Trash and purge. The filter exists because a library with forty glyphs in it makes
// finding the hero image hard, not because the storage model has a new branch.
type AssetFilter = 'all' | 'images' | 'icons' | 'fonts';
const FILTER_LABEL: Record<AssetFilter, string> = {
  all: 'All',
  images: 'Images',
  icons: 'Icons',
  fonts: 'Fonts',
};
const ACCEPT = '.png,.jpg,.jpeg,.webp,.gif,.woff2,.ttf,.otf';

export function AssetsPanel({
  appId,
  assets,
  loading,
  error,
  onRetry,
  session,
}: {
  appId: string | null;
  assets: Asset[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  session: SessionStore;
}): React.JSX.Element {
  const upload = useUploadAsset(appId);
  const input = useRef<HTMLInputElement>(null);
  const focusedStepId = useStore(session, (state) => state.focusedStepId);
  const selectionAnchor = useStore(session, (state) => state.selectionAnchor);
  const doc = useStore(session, (state) => state.doc);
  const [filter, setFilter] = useState<AssetFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setViewState] = useState<AssetExplorerView>(() =>
    readAssetExplorerView(browserAssetExplorerStorage())
  );

  const selected = assets.find((asset) => asset.id === selectedId) ?? null;
  const selectedNode = useMemo(() => {
    if (!selectionAnchor) return null;
    const step = findStep(doc, focusedStepId);
    return step ? findNode(step.root, selectionAnchor)?.node ?? null : null;
  }, [doc, focusedStepId, selectionAnchor]);
  const canUseSelected = selected?.content_type.startsWith('image/') && selectedNode?.block === 'image';
  const needle = query.trim().toLowerCase();
  const visible = assets
    .filter((asset) => {
      const kind = asset.content_type.startsWith('image/') ? 'images' : 'fonts';
      // `images` shows PHOTOGRAPHY — icons are excluded, or the tab the author reaches for when
      // looking for their hero image is the one full of glyphs. `all` still shows everything.
      const matches =
        filter === 'all'
          ? true
          : filter === 'icons'
            ? isIconAsset(asset)
            : filter === kind && !(kind === 'images' && isIconAsset(asset));
      return matches && `${assetName(asset)} ${asset.content_type}`.toLowerCase().includes(needle);
    })
    .sort((a, b) => assetName(a).localeCompare(assetName(b), undefined, { numeric: true }));

  function setView(next: AssetExplorerView): void {
    setViewState(next);
    writeAssetExplorerView(browserAssetExplorerStorage(), next);
  }

  async function uploadFiles(files: FileList | null): Promise<void> {
    for (const file of Array.from(files ?? [])) {
      if (file.size > MAX_ASSET_BYTES) {
        toast.error(`${file.name} is ${Math.round(file.size / 1024)} KB — the limit is 500 KB.`);
        continue;
      }
      try {
        await upload.mutateAsync({ file });
        toast.success(`Uploaded ${file.name}`);
      } catch (uploadError) {
        toast.error(uploadError instanceof Error ? uploadError.message : `Could not upload ${file.name}.`);
      }
    }
  }

  function applyToSelectedImage(asset: Asset): void {
    if (!selectionAnchor || selectedNode?.block !== 'image' || !asset.content_type.startsWith('image/')) return;
    session.getState().applyChange((draft) => {
      const step = findStep(draft, focusedStepId);
      const target = step ? findNode(step.root, selectionAnchor)?.node : null;
      if (target?.block === 'image') target.source = { kind: 'asset', asset_id: asset.id };
    }, `${selectionAnchor}.source`);
    toast.success(`Using ${assetName(asset)} in the selected image.`);
  }

  const emptyCopy = query.trim() || filter !== 'all' ? 'No matching assets' : 'No assets yet';

  return (
    <div className="flex min-h-full flex-col">
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          void uploadFiles(event.target.files);
          event.target.value = '';
        }}
      />

      <div className="sticky top-0 z-10 space-y-2 border-b bg-background p-2">
        <div className="grid grid-cols-4 rounded-lg border bg-muted/30 p-0.5" role="tablist" aria-label="Asset type">
          {(['all', 'images', 'icons', 'fonts'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              className={cn(
                'h-7 rounded-md text-[11px] font-medium capitalize text-muted-foreground transition-colors hover:text-foreground',
                filter === value && 'bg-secondary text-foreground shadow-sm'
              )}
              onClick={() => setFilter(value)}
            >
              {FILTER_LABEL[value]}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search assets"
              aria-label="Search assets"
              className="h-8 pl-7 text-xs"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            className="size-8 shrink-0"
            aria-label="Upload assets"
            title="Upload assets"
            disabled={!appId || upload.isPending}
            onClick={() => input.current?.click()}
          >
            <Upload className="size-3.5" />
          </Button>
        </div>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{loading ? 'Loading…' : `${visible.length} of ${assets.length}`}</span>
          <div className="flex rounded-md border p-0.5" role="group" aria-label="Asset view">
            <ViewButton label="Grid view" active={view === 'grid'} onClick={() => setView('grid')}><Grid2X2 /></ViewButton>
            <ViewButton label="List view" active={view === 'list'} onClick={() => setView('list')}><List /></ViewButton>
          </div>
        </div>
      </div>

      <div className="flex-1 p-2">
        {loading ? (
          <div className={view === 'grid' ? 'grid grid-cols-2 gap-2' : 'space-y-1'} aria-label="Loading assets">
            {[0, 1, 2, 3].map((index) => <Skeleton key={index} className={view === 'grid' ? 'h-28 rounded-md' : 'h-12 rounded-md'} />)}
          </div>
        ) : error ? (
          <PanelMessage icon={<Images />} title="Assets could not be loaded" description={error}>
            <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
          </PanelMessage>
        ) : visible.length === 0 ? (
          <PanelMessage
            icon={filter === 'fonts' ? <FileType /> : <Image />}
            title={emptyCopy}
            description={query.trim() ? `Nothing matches “${query.trim()}”.` : 'Upload an image or bundled font to get started.'}
          >
            {!query.trim() && filter === 'all' ? (
              <Button variant="outline" size="sm" disabled={!appId} onClick={() => input.current?.click()}>Upload asset</Button>
            ) : null}
          </PanelMessage>
        ) : (
          <div className={view === 'grid' ? 'grid grid-cols-2 gap-2' : 'space-y-1'}>
            {visible.map((asset) => (
              <AssetItem
                key={asset.id}
                asset={asset}
                view={view}
                selected={asset.id === selectedId}
                onSelect={() => setSelectedId((current) => current === asset.id ? null : asset.id)}
              />
            ))}
          </div>
        )}
      </div>

      {selected ? (
        <div className="sticky bottom-0 space-y-2 border-t bg-background p-2 shadow-[0_-8px_24px_-20px_rgba(0,0,0,0.7)]">
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{assetName(selected)}</div>
            <div className="truncate text-[10px] text-muted-foreground">{assetDetails(selected)}</div>
          </div>
          {canUseSelected ? (
            <Button size="sm" className="w-full" onClick={() => applyToSelectedImage(selected)}>Use in selected image</Button>
          ) : (
            <p className="text-[10px] leading-4 text-muted-foreground">
              {selected.content_type.startsWith('font/')
                ? 'Fonts become available from Theme → Type.'
                : 'Select an image block to replace its source.'}
            </p>
          )}
        </div>
      ) : null}

      <Button variant="ghost" size="sm" asChild className="m-2 mt-0 justify-start text-xs text-muted-foreground">
        <Link to="/assets">Manage asset library</Link>
      </Button>
    </div>
  );
}

function ViewButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactElement<{ className?: string }>;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn('grid size-6 place-items-center rounded text-muted-foreground hover:text-foreground', active && 'bg-secondary text-foreground')}
      onClick={onClick}
    >
      <span className="[&>svg]:size-3.5">{children}</span>
    </button>
  );
}

function AssetItem({
  asset,
  view,
  selected,
  onSelect,
}: {
  asset: Asset;
  view: AssetExplorerView;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  if (view === 'list') {
    return (
      <button
        type="button"
        aria-label={`Select ${assetName(asset)}`}
        aria-pressed={selected}
        className={cn('flex w-full items-center gap-2 rounded-md border border-transparent p-1 text-left hover:bg-secondary/50', selected && 'border-primary/50 bg-secondary')}
        onClick={onSelect}
      >
        <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded border bg-muted/40">
          <AssetPreview asset={asset} decorative className="size-full object-cover" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[11px] font-medium">{assetName(asset)}</span>
          <span className="block truncate text-[9px] uppercase tracking-wide text-muted-foreground">{assetDetails(asset)}</span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={`Select ${assetName(asset)}`}
      aria-pressed={selected}
      className={cn('overflow-hidden rounded-md border bg-background text-left transition-colors hover:border-muted-foreground/50', selected && 'border-primary ring-1 ring-primary/30')}
      onClick={onSelect}
    >
      <span className="grid aspect-[4/3] place-items-center overflow-hidden bg-muted/40">
        <AssetPreview asset={asset} decorative className="size-full object-cover" />
      </span>
      <span className="block min-w-0 border-t px-1.5 py-1.5">
        <span className="block truncate text-[10px] font-medium">{assetName(asset)}</span>
        <span className="block truncate text-[8px] uppercase tracking-wide text-muted-foreground">{assetDetails(asset)}</span>
      </span>
    </button>
  );
}

function PanelMessage({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-3 py-8 text-center">
      <span className="grid size-8 place-items-center rounded-md bg-secondary text-muted-foreground [&>svg]:size-4">{icon}</span>
      <div>
        <div className="text-xs font-medium">{title}</div>
        <p className="mt-1 text-[10px] leading-4 text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}
